import ChipCell from '@app/src/components/Table/Cells/ChipCell'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import AutomationMenu from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationMenu'
import type { AutomationCardItem } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationsConfigLogic'
import { getExpectationCardSummaryLabelId } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/ExpectationAutomation/expectationAutomationLogic'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import type { RequestAutomation } from '@app/src/types/automations'
import type { ExpectationAutomation } from '@app/src/types/expectations'
import { formatLevelLabel } from '@app/src/utils/assessmentLevelTranslations'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import { Box, Card, CardActionArea, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import AutomationDisplaySection from './components/AutomationDisplaySection'

interface RequestDetailsProps {
  automation: RequestAutomation
  isInactive: boolean
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ automation, isInactive }) => {
  const { formatMessage, formatDate } = useIntl()
  const { getPeriodDisplayName } = usePeriodName()

  const periodName =
    automation.periodStartsAt && automation.periodEndsAt
      ? getPeriodDisplayName(automation.periodStartsAt, automation.periodEndsAt)
      : undefined

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: isInactive ? 0.5 : 1 }}>
        <AutomationDisplaySection automation={automation} />
        {automation.automationType !== RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL &&
          automation.automationType !== RequestAutomationType.AI_ENRICH && (
            <>
              <Typography variant="body1" fontWeight="bold">
                →
              </Typography>
              <Typography variant="body1" fontWeight="bold" whiteSpace="nowrap">
                {formatMessage({
                  id:
                    automation.automationType === RequestAutomationType.AI_SCREEN
                      ? 'automatedSendout.screenForQuestionnaire'
                      : 'automatedSendout.sendQuestionnaire',
                })}
              </Typography>
            </>
          )}
        {automation.automationType === RequestAutomationType.AI_ENRICH && (
          <>
            <Typography variant="body1" fontWeight="bold">
              →
            </Typography>
            <Typography variant="body1" fontWeight="bold" whiteSpace="nowrap">
              {formatMessage({ id: 'automatedSendout.enrichProperties' })}
            </Typography>
          </>
        )}
      </Stack>

      {automation.customDueDate ? (
        <Typography sx={{ opacity: isInactive ? 0.5 : 1 }}>
          {formatMessage(
            { id: 'automatedSendout.customDueDate' },
            {
              date: formatDate(getDateWithoutTimeFromString(automation.customDueDate), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            },
          )}
        </Typography>
      ) : (
        Boolean(automation.deadlineOffSetDays) && (
          <Typography sx={{ opacity: isInactive ? 0.5 : 1 }}>
            {formatMessage(
              {
                id:
                  automation.automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY
                    ? 'automatedSendout.dueDateCategory'
                    : automation.automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL
                      ? 'automatedSendout.dueDateAssessment'
                      : 'automatedSendout.dueDateNewCompany',
              },
              { days: automation.deadlineOffSetDays },
            )}
          </Typography>
        )
      )}
      {automation.automationType !== RequestAutomationType.AI_SCREEN &&
        automation.automationType !== RequestAutomationType.AI_ENRICH &&
        Boolean(periodName) && (
          <Typography sx={{ opacity: isInactive ? 0.5 : 1 }}>
            {formatMessage({ id: 'automatedSendout.period' }, { period: periodName })}
          </Typography>
        )}
    </>
  )
}

interface ExpectationDetailsProps {
  automation: ExpectationAutomation
  isInactive: boolean
}

const ExpectationDetails: React.FC<ExpectationDetailsProps> = ({ automation, isInactive }) => {
  const { formatMessage, formatDate } = useIntl()
  const providerType = useCurrentProviderType()

  const { multiAssessmentExpectation } = useFlags()
  const summaryLabelId = getExpectationCardSummaryLabelId(automation.highestAllowedProviderRiskStatus)

  return (
    <>
      {multiAssessmentExpectation && automation.assessmentTemplate && (
        <Typography variant="body2" color="text.secondary" sx={{ opacity: isInactive ? 0.5 : 1 }}>
          {automation.assessmentTemplate.name}
        </Typography>
      )}
      <Typography variant="body1" fontWeight="bold" whiteSpace="pre-line" sx={{ opacity: isInactive ? 0.5 : 1 }}>
        {formatMessage(
          { id: summaryLabelId },
          {
            providerType: providerType,
            level: formatLevelLabel(automation.assessmentTemplate, automation.targetAssessmentLevel, formatMessage),
          },
        )}
      </Typography>
      <Typography sx={{ opacity: isInactive ? 0.5 : 1 }}>
        {automation.customDueDate
          ? formatMessage(
              { id: 'automatedSendout.expectation.customDueDate' },
              {
                date: formatDate(getDateWithoutTimeFromString(automation.customDueDate), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              },
            )
          : formatMessage(
              { id: 'automatedSendout.expectation.deadlineYears' },
              { years: automation.deadlineYearsOffset },
            )}
      </Typography>
    </>
  )
}

interface AutomationCardProps {
  automation: AutomationCardItem
  editAutomation?: () => void
  onDeleted?: () => void
}

const AutomationCard: React.FC<AutomationCardProps> = ({ automation, editAutomation, onDeleted }) => {
  const { formatMessage, formatDate } = useIntl()

  const isInactive = !automation.isActive

  const requestChips = (
    <Stack direction="row" flexWrap="wrap" alignItems="center">
      {automation.templateNames?.slice(0, 3)?.map(templateName => (
        <Chip sx={{ mb: 1, mr: 1, opacity: isInactive ? 0.5 : 1 }} label={templateName} key={templateName} />
      ))}
      {automation.templateNames.length > 3 && (
        <Tooltip title={automation.templateNames.map(templateName => templateName).join(', ')}>
          <Typography variant="body1" mb={1} sx={{ opacity: isInactive ? 0.5 : 1 }}>
            {formatMessage({ id: 'automatedSendout.plusMore' }, { count: automation.templateNames.length - 3 })}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  )

  const cardContent = (
    <Card
      elevation={0}
      variant="outlined"
      sx={{
        marginBottom: 2,
        height: '100%',
      }}
    >
      <CardActionArea
        sx={{
          height: '100%',
          padding: 2,
          lineHeight: 3,
          cursor: 'default',
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0,
          },
        }}
      >
        <Stack height="100%" justifyContent="space-between">
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="textSecondary">
                {formatMessage(
                  { id: automation.updatedAt ? 'automatedSendout.updatedAt' : 'automatedSendout.createdAt' },
                  {
                    date: formatDate(automation.updatedAt ?? automation.createdAt, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }),
                  },
                )}
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <ChipCell
                  value={formatMessage({ id: `automatedSendout.status.${automation.isActive}` })}
                  disableCell
                  size="small"
                  color={automation.isActive ? 'success' : 'default'}
                  aria-label={formatMessage({ id: `automatedSendout.status.${automation.isActive}` })}
                />
                <AutomationMenu
                  automation={automation}
                  editAutomation={editAutomation}
                  onDeleted={onDeleted}
                  isActive={automation.isActive}
                />
              </Box>
            </Stack>
            {automation.requestAutomation ? (
              <RequestDetails automation={automation.requestAutomation} isInactive={isInactive} />
            ) : automation.expectationAutomation ? (
              <ExpectationDetails automation={automation.expectationAutomation} isInactive={isInactive} />
            ) : null}
          </Stack>
          {automation.requestAutomation?.automationType !== RequestAutomationType.AI_ENRICH &&
            Boolean(requestChips) &&
            requestChips}
        </Stack>
      </CardActionArea>
    </Card>
  )

  return isInactive ? (
    <Tooltip title={formatMessage({ id: 'automatedSendout.inactiveTooltip' })} placement="top" arrow>
      {cardContent}
    </Tooltip>
  ) : (
    cardContent
  )
}

export default AutomationCard
